import {parsePresDetailsLayout} from './helpers';
import {useLsValue} from '../../../../../hooks/use-ls-value';
import {LS_PREFIX} from '../../../../../providers/global-state';
import {GraphLayout, GridCoordinates} from '../../../../../components/graph/interface';
import React from 'react';
import {GraphPresentation, GraphPresentationDetails} from './interface';
import usePreviousDistinct from 'react-use/lib/usePreviousDistinct';
import {chain} from '@genestack/ui';

interface Props {
    queryId: number;
    selectedPresentation: GraphPresentation;
    selectedPresentationDetails: GraphPresentationDetails;
    isHiddenNodesVisible: boolean;
    isHiddenNode: (nodeId: number) => boolean;
}

export function useNodePositionsState(props: Props) {
    const {
        selectedPresentationDetails,
        isHiddenNode,
        isHiddenNodesVisible,
        queryId,
        selectedPresentation
    } = props;

    const {
        visibleNodesPositions: visibleNodesPositionsDefault,
        invisibleNodesPositions: invisibleNodesPositionsDefault
    } = parsePresDetailsLayout(
        selectedPresentationDetails.layout,
        isHiddenNodesVisible,
        isHiddenNode
    );
    const [visibleNodesPositions, setVisibleNodesPositions, resetVisibleNodesPositions] =
        useLsValue<string, [number, number], []>({
            changeEntityParams: [queryId, selectedPresentation.id],
            rewriteKeyParams: [],
            getKey: ([qId, pId]) => `${LS_PREFIX}.queryGraph.${qId}.presentation${pId}.layout`,
            defaultValue: visibleNodesPositionsDefault,
            validateValue: (value) =>
                !value ||
                value === '{}' ||
                Object.values(JSON.parse(value) as GraphLayout).every(
                    (coordinates: GridCoordinates) =>
                        !Number.isNaN(coordinates.x) && !Number.isNaN(coordinates.y)
                )
        });

    const clearVisibleNodesPositions = React.useCallback(() => {
        setVisibleNodesPositions('{}');
    }, [setVisibleNodesPositions]);

    const [invisibleNodesPositions, setInvisibleNodesPositions, clearInvisibleNodesPositions] =
        useLsValue<string, [number, number], []>({
            changeEntityParams: [queryId, selectedPresentation.id],
            rewriteKeyParams: [],
            getKey: ([qId, pId]) =>
                `${LS_PREFIX}.queryGraph.${qId}.presentation${pId}.invisibleNodesPositions`,
            defaultValue: invisibleNodesPositionsDefault,
            validateValue: (value) =>
                !value ||
                Object.values(JSON.parse(value) as GraphLayout).every(
                    (coordinates: GridCoordinates) =>
                        !Number.isNaN(coordinates.x) && !Number.isNaN(coordinates.y)
                )
        });

    const prevVisibleNodesPositions = usePreviousDistinct(visibleNodesPositions);
    const prevInvisibleNodesPositions = usePreviousDistinct(invisibleNodesPositions);

    const parsedVisibleNodesPositions = visibleNodesPositions
        ? (JSON.parse(visibleNodesPositions) as GraphLayout)
        : {};

    return {
        visibleNodesPositions: !Object.keys(parsedVisibleNodesPositions).length
            ? undefined
            : parsedVisibleNodesPositions,
        invisibleNodesPositions: invisibleNodesPositions
            ? (JSON.parse(invisibleNodesPositions) as GraphLayout)
            : undefined,
        resetNodePositions: chain(resetVisibleNodesPositions, clearInvisibleNodesPositions)!,
        setVisibleNodesPositions,
        clearVisibleNodesPositions,
        setInvisibleNodesPositions,
        prevVisibleNodesPositions,
        prevInvisibleNodesPositions
    };
}
